import React from "react";
import { MdAdd, MdArrowBack, MdDelete, MdDragIndicator } from "react-icons/md";
import Card from "components/card";
import { reportTemplateService } from "services/admin/reportTemplateService";
import type { FieldType, ReportTemplate } from "types/domain";
import { useLanguage } from "context/LanguageContext";

interface FieldRow {
  key: string; // local key for React
  label: string;
  field_type: FieldType;
  sort_order: number;
  is_required: boolean;
  config: Record<string, unknown> | null;
}

interface Props {
  template: ReportTemplate | null;
  onCancel: () => void;
  onSave: () => void;
}

const FIELD_TYPE_OPTIONS: { value: FieldType; labelKey: string }[] = [
  { value: "TEXT", labelKey: "reportTemplate.fieldType.TEXT" },
  { value: "NUMBER", labelKey: "reportTemplate.fieldType.NUMBER" },
  { value: "FILE", labelKey: "reportTemplate.fieldType.FILE" },
  {
    value: "MULTIPLE_FILES",
    labelKey: "reportTemplate.fieldType.MULTIPLE_FILES",
  },
];

const DEFAULT_MAX_FILES = 5;

let fieldKeyCounter = 0;
const nextKey = () => `field-${++fieldKeyCounter}`;

const ReportTemplateForm: React.FC<Props> = ({
  template,
  onCancel,
  onSave,
}) => {
  const { t } = useLanguage();
  const [name, setName] = React.useState(template?.name ?? "");
  const [description, setDescription] = React.useState(
    template?.description ?? ""
  );
  const [isDefault, setIsDefault] = React.useState(
    template?.is_default ?? false
  );
  const [fields, setFields] = React.useState<FieldRow[]>(() => {
    if (template?.fields?.length) {
      return template.fields
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((f) => ({
          key: nextKey(),
          label: f.label,
          field_type: f.field_type,
          sort_order: f.sort_order,
          is_required: f.is_required,
          config: (f.config as Record<string, unknown>) ?? null,
        }));
    }
    return [];
  });
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const addField = () => {
    setFields((prev) => [
      ...prev,
      {
        key: nextKey(),
        label: "",
        field_type: "TEXT",
        sort_order: prev.length,
        is_required: false,
        config: null,
      },
    ]);
  };

  const removeField = (key: string) => {
    setFields((prev) =>
      prev.filter((f) => f.key !== key).map((f, i) => ({ ...f, sort_order: i }))
    );
  };

  const updateField = (key: string, updates: Partial<FieldRow>) => {
    setFields((prev) =>
      prev.map((f) => (f.key === key ? { ...f, ...updates } : f))
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError(t("reportTemplate.nameRequired"));
      return;
    }
    if (fields.some((f) => !f.label.trim())) {
      setError(t("reportTemplate.fieldLabelRequired"));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        is_default: isDefault,
        fields: fields.map((f, i) => ({
          label: f.label.trim(),
          field_type: f.field_type,
          sort_order: i,
          is_required: f.is_required,
          config: f.config,
        })),
      };

      if (template) {
        await reportTemplateService.update(template.id, payload);
      } else {
        await reportTemplateService.create(payload);
      }
      onSave();
    } catch (err: any) {
      setError(err.message ?? t("reportTemplate.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3">
      <Card extra="p-6">
        <div className="mb-6 flex items-center gap-3">
          <button
            onClick={onCancel}
            className="rounded p-1.5 text-ink-muted hover:bg-surface-hover"
          >
            <MdArrowBack className="h-5 w-5" />
          </button>
          <h2 className="text-xl font-bold text-ink">
            {template
              ? t("reportTemplate.editTitle")
              : t("reportTemplate.createTitle")}
          </h2>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="mb-1 block text-sm font-medium text-ink-muted">
              {t("reportTemplate.name")} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-line px-3 py-2 text-sm dark:bg-surface-muted dark:text-white"
              placeholder={t("reportTemplate.namePlaceholder")}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-ink-muted">
              {t("reportTemplate.description")}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-line dark:bg-surface-muted dark:text-white"
              placeholder={t("reportTemplate.descriptionPlaceholder")}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_default"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              className="h-4 w-4 rounded"
            />
            <label
              htmlFor="is_default"
              className="text-sm text-ink-muted"
            >
              {t("reportTemplate.isDefault")}
            </label>
          </div>

          {/* Fields */}
          <div>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-ink">
                {t("reportTemplate.fields")}
              </h3>
              <button
                type="button"
                onClick={addField}
                className="flex items-center gap-1 rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-ink-muted hover:bg-gray-200 dark:bg-surface-hover dark:hover:bg-navy-500"
              >
                <MdAdd className="h-4 w-4" />
                {t("reportTemplate.addField")}
              </button>
            </div>

            {fields.length === 0 && (
              <p className="text-sm text-ink-subtle">
                {t("reportTemplate.noFields")}
              </p>
            )}

            <div className="space-y-3">
              {fields.map((field, idx) => (
                <div
                  key={field.key}
                  className="flex items-start gap-3 rounded-lg border border-line p-3"
                >
                  <MdDragIndicator className="mt-2.5 h-5 w-5 shrink-0 text-ink-subtle" />
                  <div className="flex-1 space-y-2">
                    <div className="flex gap-3">
                      <input
                        type="text"
                        value={field.label}
                        onChange={(e) =>
                          updateField(field.key, { label: e.target.value })
                        }
                        placeholder={t("reportTemplate.fieldLabel")}
                        className="flex-1 rounded border border-line px-2 py-1.5 text-sm dark:bg-surface-muted dark:text-white"
                      />
                      <select
                        value={field.field_type}
                        onChange={(e) =>
                          updateField(field.key, {
                            field_type: e.target.value as FieldType,
                          })
                        }
                        className="rounded border border-line px-2 py-1.5 text-sm dark:bg-surface-muted dark:text-white"
                      >
                        {FIELD_TYPE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {t(opt.labelKey)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={field.is_required}
                        onChange={(e) =>
                          updateField(field.key, {
                            is_required: e.target.checked,
                          })
                        }
                        className="h-3.5 w-3.5 rounded"
                      />
                      <span className="text-xs text-ink-muted">
                        {t("reportTemplate.required")}
                      </span>
                    </div>
                    {field.field_type === "MULTIPLE_FILES" && (
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-ink-muted">
                          {t("reportTemplate.maxFiles")}
                        </label>
                        <input
                          type="number"
                          min={1}
                          placeholder={String(DEFAULT_MAX_FILES)}
                          value={
                            (field.config?.max_files as number | undefined) ??
                            ""
                          }
                          onChange={(e) =>
                            updateField(field.key, {
                              config: {
                                ...field.config,
                                max_files: e.target.value
                                  ? parseInt(e.target.value, 10)
                                  : undefined,
                              },
                            })
                          }
                          className="w-20 rounded border border-line px-2 py-1 text-xs dark:bg-surface-muted dark:text-white"
                        />
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeField(field.key)}
                    className="rounded p-1 text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <MdDelete className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-brand-500 px-6 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {saving
                ? t("action.saving")
                : template
                ? t("reportTemplate.update")
                : t("reportTemplate.submitCreate")}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-line px-6 py-2 text-sm font-medium text-ink-muted hover:bg-surface-hover"
            >
              {t("action.cancel")}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default ReportTemplateForm;
